import { authClient } from "@/lib/auth-client";

/**
 * Session Manager for cross-tab coordination
 * 
 * Updates for 2.0:
 * - Removed manual DOM activity tracking (relying on Better Auth session validity)
 * - Simplified event handling
 * - Retained BroadcastChannel for instant cross-tab UI synchronization
 */

export type SessionEventType = 
  | 'user_logout'           // User clicked logout button
  | 'security_logout'       // Admin/security system terminated session
  | 'session_expired'       // Session naturally expired
  | 'auth_flow_active';     // Authentication flow in progress

export interface SessionEvent {
  type: SessionEventType;
  tabId: string;
  timestamp: number;
  sessionId?: string;
  reason?: string;
}

export class SessionManager {
  private static instance: SessionManager;
  private tabId: string;
  private channel: BroadcastChannel | null = null;
  private activityCheckInterval: number | null = null;
  
  // Polling interval to reconcile with server state (e.g. remote revocation)
  private readonly HEALTH_CHECK_INTERVAL = 30000; // 30 seconds

  private constructor() {
    this.tabId = this.generateTabId();
    this.initializeBroadcastChannel();
    this.startHealthCheck();
  }

  static getInstance(): SessionManager {
    if (!SessionManager.instance) {
      SessionManager.instance = new SessionManager();
    }
    return SessionManager.instance;
  }

  private generateTabId(): string {
    return `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private initializeBroadcastChannel(): void {
    try {
      this.channel = new BroadcastChannel('howly_session_coordination');
      
      this.channel.onmessage = (event) => {
        const sessionEvent: SessionEvent = event.data;
        
        // Ignore events from this tab
        if (sessionEvent.tabId === this.tabId) return;

        this.handleSessionEvent(sessionEvent);
      };

      console.log('[SessionManager] Initialized with tabId:', this.tabId);
    } catch (error) {
      console.error('[SessionManager] BroadcastChannel not supported:', error);
    }
  }

  private startHealthCheck(): void {
    // Periodically check session health via Better Auth
    this.activityCheckInterval = window.setInterval(() => {
      this.checkSessionHealth();
    }, this.HEALTH_CHECK_INTERVAL);
  }

  private async checkSessionHealth(): Promise<void> {
    try {
      // Rely on Better Auth as the source of truth
      const { data, error } = await authClient.getSession();
      const session = data?.session;
      
      // If we have a local token but Better Auth says no session, we are expired/revoked
      // Note: We check sessionStorage to see if we *expect* to be logged in to avoid false positives on public pages
      const wasLoggedIn = sessionStorage.getItem('auth_flow_active') === 'true' || localStorage.getItem('better-auth.session_token');

      if (!session && wasLoggedIn) {
        console.warn('[SessionManager] Session invalid or expired on server');
        this.handleSessionLoss('session_expired');
      }
    } catch (error) {
      console.error('[SessionManager] Session health check failed:', error);
    }
  }

  private handleSessionEvent(event: SessionEvent): void {
    console.log('[SessionManager] Processing event:', event.type);

    switch (event.type) {
      case 'security_logout':
      case 'user_logout':
        // Immediate cleanup for all tabs
        this.handleLocalLogout(event.reason);
        break;

      case 'session_expired':
        this.handleLocalLogout('Session expired');
        break;

      case 'auth_flow_active':
        // Just logging for debugging, no action needed
        // This helps debug race conditions if multiple tabs try to refresh tokens
        break;
    }
  }

  private async handleSessionLoss(reason: string): Promise<void> {
    // Broadcast to other tabs so they don't all hit the API simultaneously
    this.broadcastEvent({
      type: 'session_expired',
      reason,
    });

    await this.handleLocalLogout(reason);
  }

  private async handleLocalLogout(reason?: string): Promise<void> {
    console.log('[SessionManager] Processing local logout:', reason);
    
    // Clear local state markers
    sessionStorage.removeItem('admin_last_2fa_verification');
    sessionStorage.removeItem('auth_flow_active');
    sessionStorage.removeItem('auth_flow_state');
    
    // Redirect if we are currently on a protected route
    if (window.location.pathname !== '/auth') {
      window.location.href = '/auth';
    }
  }

  public broadcastEvent(event: Pick<SessionEvent, 'type' | 'reason' | 'sessionId'>): void {
    if (!this.channel) return;

    const fullEvent: SessionEvent = {
      type: event.type,
      reason: event.reason,
      sessionId: event.sessionId,
      tabId: this.tabId,
      timestamp: Date.now(),
    };

    this.channel.postMessage(fullEvent);
  }

  public notifyAuthFlowStart(): void {
    sessionStorage.setItem('auth_flow_active', 'true');
    this.broadcastEvent({ type: 'auth_flow_active' });
  }

  public notifyUserLogout(): void {
    this.broadcastEvent({ type: 'user_logout' });
    this.handleLocalLogout('User initiated logout');
  }

  public notifySecurityLogout(reason: string): void {
    this.broadcastEvent({ type: 'security_logout', reason });
    this.handleLocalLogout(reason);
  }

  public cleanup(): void {
    if (this.activityCheckInterval) {
      clearInterval(this.activityCheckInterval);
    }
    if (this.channel) {
      this.channel.close();
    }
  }
}

// Export singleton instance
export const sessionManager = SessionManager.getInstance();
